import {
  AuthenticationResult,
  AuthError,
  CacheLookupPolicy,
  Configuration,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AuthenticationProvider } from '@microsoft/microsoft-graph-client';
import { GraphLoginException } from 'common/exceptions/graph/GraphLoginException';
import { GraphLoginResult } from 'common/models/GraphLoginResult';
import CONFIG from 'config';
import { assertHasGraphApi } from './assertHasGraphApi';
import { Dialog } from './office-js-helpers/dialog';

const TAG = 'MSA3: ';

/**
 * Configuration class for @azure/msal-browser:
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
 */
const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: CONFIG.graph.client_id,
    redirectUri: CONFIG.urls.graphLogin,
  },
  cache: {
    cacheLocation: 'localStorage', // needed to avoid "login required" error
    storeAuthStateInCookie: true, // recommended to avoid certain IE/Edge issues
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }

        switch (level) {
          case LogLevel.Error:
            console.error(TAG, message);
            return;
          case LogLevel.Info:
            console.info(TAG, message);
            return;
          case LogLevel.Verbose:
            console.debug(TAG, message);
            return;
          case LogLevel.Warning:
            console.warn(TAG, message);
            return;
        }
      },
    },
  },
};

const SCOPES = [
  'Calendars.Read.Shared',
  'Contacts.ReadWrite',
  'Mail.ReadWrite',
];

const clientApplication = new PublicClientApplication(MSAL_CONFIG);

/**
 * Checks whether we are in the middle of a redirect and handles state accordingly. Only required for redirect flows.
 *
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#redirect-apis
 */
export async function handleRedirectPromise(): Promise<AuthenticationResult> {
  await clientApplication.initialize();

  const resp = await clientApplication.handleRedirectPromise();
  return handleResponse(resp);
}

/**
 * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
 * @param response
 */
function handleResponse(response: AuthenticationResult | null) {
  if (response !== null) {
    clientApplication.setActiveAccount(response.account);
  }

  return response;
}

export function loginRedirect(): void {
  clientApplication.loginRedirect({
    scopes: SCOPES,
  });
}

async function openLoginDialog(): Promise<void> {
  const resultString = await new Dialog<string>(CONFIG.urls.graphLogin).result;

  const result: GraphLoginResult = JSON.parse(resultString);

  if (!result.success) {
    throw result.error;
  }

  for (const property in result.storage) {
    localStorage.setItem(property, result.storage[property]);
  }

  console.log(TAG, 'Graph login succeeded');
}

async function getToken(retry: boolean): Promise<string> {
  assertHasGraphApi();

  try {
    await clientApplication.initialize();

    const result = await clientApplication.acquireTokenSilent({
      scopes: SCOPES,
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    });

    return result.accessToken;
  } catch (error: unknown) {
    if (retry && error instanceof AuthError) {
      console.log(TAG, 'acquireTokenSilent failed, retrying', error);
      await openLoginDialog();
      return await getToken(false);
    }

    throw new GraphLoginException(
      'Microsoft Graph Token Acquisition Failed',
      error
    );
  }
}

export class GraphAuthenticationProvider implements AuthenticationProvider {
  async getAccessToken(): Promise<string> {
    return await getToken(true);
  }
}
